import { type JSX, useCallback } from "react";
import { t } from "ttag";

import { getUserIsAdmin } from "metabase/current-user";
import type { PLUGIN_ENTITY_ICON } from "metabase/hooks/plugins";
import { PLUGIN_REMOTE_SYNC } from "metabase/plugins";
import { useSelector } from "metabase/redux";
import type Question from "metabase-lib/v1/Question";
import type { Card, Dashboard } from "metabase-types/api";

import { VerifyMenuItem } from "./components/VerifyMenuItem/VerifyMenuItem";
import { OFFICIAL_COLLECTION_ICON } from "./constants";
import { getVerifyCardLabel, isOfficialCollection, isVerified } from "./utils";

const VERIFICATION_MENU_ITEM_KEY = "verification";

/**
 * Whether `item` is synced from Git on an instance that only imports from
 * Git. Its verification comes from Git, so it can't be changed here.
 */
function useIsReadOnlySynced(item?: { is_remote_synced?: boolean }): boolean {
  const isRemoteSyncReadOnly = useSelector(
    PLUGIN_REMOTE_SYNC.getIsRemoteSyncReadOnly,
  );
  return isRemoteSyncReadOnly && item?.is_remote_synced === true;
}

/** The verify or unverify action for a saved question, model or metric; admins only. */
export function useCardMenuItems(
  card?: Card,
  reload?: () => void,
): JSX.Element[] {
  const isAdmin = useSelector(getUserIsAdmin);
  const isReadOnlySynced = useIsReadOnlySynced(card);

  if (!card?.id || card.archived || !isAdmin || isReadOnlySynced) {
    return [];
  }

  return [
    <VerifyMenuItem
      key={VERIFICATION_MENU_ITEM_KEY}
      itemType="card"
      itemId={card.id}
      isVerified={isVerified(card.moderation_reviews)}
      verifyLabel={getVerifyCardLabel(card.type)}
      onChange={reload}
    />,
  ];
}

export function useQuestionMenuItems(
  question?: Question,
  reload?: () => void,
): JSX.Element[] {
  return useCardMenuItems(question?.card(), reload);
}

/** The verify or unverify action for a saved dashboard; admins only. */
export function useDashboardMenuItems(
  dashboard?: Dashboard,
  reload?: () => void,
): JSX.Element[] {
  const isAdmin = useSelector(getUserIsAdmin);
  const isReadOnlySynced = useIsReadOnlySynced(dashboard);

  // public and embedded dashboards are addressed by a string id and can't be verified
  if (!dashboard || typeof dashboard.id !== "number") {
    return [];
  }

  if (dashboard.archived || !isAdmin || isReadOnlySynced) {
    return [];
  }

  return [
    <VerifyMenuItem
      key={VERIFICATION_MENU_ITEM_KEY}
      itemType="dashboard"
      itemId={dashboard.id}
      isVerified={isVerified(dashboard.moderation_reviews)}
      verifyLabel={t`Verify this dashboard`}
      onChange={reload}
    />,
  ];
}

type UseGetIcon = (typeof PLUGIN_ENTITY_ICON)["useGetIcon"];

/**
 * Wraps the entity icon hook so Official collections get the Official icon in
 * breadcrumbs, pickers and the command palette, and leaves every other icon
 * to `useGetIconFallback`.
 */
export function createUseGetIcon(useGetIconFallback: UseGetIcon): UseGetIcon {
  return function useGetIconWithOfficialCollections() {
    const getIconFallback = useGetIconFallback();

    return useCallback<ReturnType<UseGetIcon>>(
      (item, opts) =>
        item.model === "collection" && isOfficialCollection(item)
          ? OFFICIAL_COLLECTION_ICON
          : getIconFallback(item, opts),
      [getIconFallback],
    );
  };
}
